import { useCallback, useEffect, useRef } from 'react';
import { notification } from 'antd';
import { store } from '../../../store/store';
import {
  selectRefreshToken,
  selectRememberMe,
  selectRoles,
  selectToken,
  selectUser,
} from '../../../store/reducers/AuthSlice';
import { refreshAccessToken } from '../../../utils/http';
import { getAuthSessionGeneration } from '../../../utils/http/authSession';
import { buildPermissions } from '../../shared/permissions';
import { openAnalytics } from './openAnalytics';

const errorMessages = {
  'missing-token':
    'Сессия MedVox изменилась. Повторите вход и переход в аналитику.',
  blocked: 'Разрешите открытие новых вкладок для MedVox и повторите переход.',
  timeout:
    'Аналитика не ответила. Повторите переход или обратитесь к администратору.',
  failed: 'Не удалось передать авторизацию. Повторите переход в аналитику.',
} as const;

const getAccessToken = () => {
  const state = store.getState();
  return buildPermissions(selectRoles(state)).canViewAnalytics
    ? selectToken(state)
    : null;
};

const getFreshAccessToken = async () => {
  const state = store.getState();
  if (!getAccessToken()) return null;
  return refreshAccessToken({
    dispatch: store.dispatch,
    refreshToken: selectRefreshToken(state),
    rememberMe: selectRememberMe(state),
  });
};

export const useAnalyticsNavigation = () => {
  const pending = useRef(new Set<AbortController>());

  useEffect(() => {
    const requests = pending.current;
    const cancel = () => {
      requests.forEach((controller) => controller.abort());
      requests.clear();
    };
    let generation = getAuthSessionGeneration();
    let user = selectUser(store.getState());
    const unsubscribe = store.subscribe(() => {
      const nextGeneration = getAuthSessionGeneration();
      const nextUser = selectUser(store.getState());
      if (
        !getAccessToken() ||
        nextGeneration !== generation ||
        nextUser !== user
      ) {
        cancel();
      }
      generation = nextGeneration;
      user = nextUser;
    });
    return () => {
      unsubscribe();
      cancel();
    };
  }, []);

  return useCallback((path: string) => {
    const controller = new AbortController();
    pending.current.add(controller);
    void openAnalytics(
      path,
      { getAccessToken, getFreshAccessToken },
      controller.signal,
    )
      .then((result) => {
        if (result !== 'sent' && result !== 'cancelled') {
          notification.error({
            message: 'Переход в аналитику',
            description: errorMessages[result],
          });
        }
      })
      .catch(() => {
        notification.error({
          message: 'Переход в аналитику',
          description: errorMessages.failed,
        });
      })
      .finally(() => pending.current.delete(controller));
  }, []);
};
